import {
  ActionIcon,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconMapPin, IconPlus, IconTrash } from "@tabler/icons-react";
import FeatureUploadImages from "components/ImageUploader/FeatureUploadImages";
import FormModal from "components/FormModal";
import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { DocumentWithId, createDocumentWithId } from "types/document";

export interface TrafficAccidentReportForm {
  title: string;
  message: string;
  accidentLocation: string;
  otherPartyName: string;
  otherPartyPhone: string;
  otherPartyEmail: string;
  otherPartyOtherInfo: string;
  vehiclePhotos: DocumentWithId[];
  otherPartyPhotos: DocumentWithId[];
}

interface FeatureSendTrafficAccidentReportProps {
  onSend: (values: TrafficAccidentReportForm) => Promise<void>;
  isPending: boolean;
  trigger: ReactElement<{ onClick?: () => void }>;
}

export const FeatureSendTrafficAccidentReport: React.FC<
  FeatureSendTrafficAccidentReportProps
> = ({ onSend, isPending, trigger }) => {
  const { t } = useTranslation("news");
  const { t: tMessage } = useTranslation("message");
  const [opened, { toggle }] = useDisclosure();
  const [showOtherParty, { open: openOtherParty, close: closeOtherParty }] =
    useDisclosure();

  const form = useForm<TrafficAccidentReportForm>({
    initialValues: {
      title: "",
      message: "",
      accidentLocation: "",
      otherPartyName: "",
      otherPartyPhone: "",
      otherPartyEmail: "",
      otherPartyOtherInfo: "",
      vehiclePhotos: [],
      otherPartyPhotos: [],
    },
    validate: {
      title: isNotEmpty(),
      message: isNotEmpty(),
      accidentLocation: isNotEmpty(),
    },
  });

  const appendVehiclePhoto = (blob: Blob) => {
    form.insertListItem("vehiclePhotos", createDocumentWithId(blob));
  };

  const removeVehiclePhoto = (id: string) => {
    const index = form.values.vehiclePhotos.findIndex(doc => doc.id === id);
    if (index !== -1) form.removeListItem("vehiclePhotos", index);
  };

  const appendOtherPartyPhoto = (blob: Blob) => {
    form.insertListItem("otherPartyPhotos", createDocumentWithId(blob));
  };

  const removeOtherPartyPhoto = (id: string) => {
    const index = form.values.otherPartyPhotos.findIndex(doc => doc.id === id);
    if (index !== -1) form.removeListItem("otherPartyPhotos", index);
  };

  const handleRemoveOtherParty = () => {
    form.setValues({
      otherPartyName: "",
      otherPartyPhone: "",
      otherPartyEmail: "",
      otherPartyOtherInfo: "",
    });
    closeOtherParty();
  };

  const handleClose = () => {
    form.reset();
    closeOtherParty();
    toggle();
  };

  const handleSubmit = async (values: TrafficAccidentReportForm) => {
    try {
      await onSend(values);
      handleClose();
    } catch {
      // onSend handles error feedback; keep modal open
    }
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: toggle })}

      {opened && (
        <FormModal
          xOffset={0}
          gap={20}
          onSubmit={form.onSubmit(handleSubmit)}
          title={t("sendTrafficAccidentReport")}
          opened={opened}
          onClose={handleClose}
          saveButtonLabel={tMessage("send").toLocaleUpperCase()}
          isPending={isPending}
        >
          <Stack px={20} gap="sm">
            <TextInput
              {...form.getInputProps("title")}
              required
              label={tMessage("title")}
              placeholder={tMessage("title")}
            />
            <Textarea
              {...form.getInputProps("message")}
              required
              label={tMessage("message")}
              placeholder={tMessage("message")}
              rows={5}
            />
            <TextInput
              {...form.getInputProps("accidentLocation")}
              required
              label={t("accidentLocation")}
              placeholder={t("accidentLocationPlaceholder")}
              rightSection={<IconMapPin size={18} className="text-red" />}
            />

            {!showOtherParty ? (
              <UnstyledButton
                onClick={openOtherParty}
                className="flex items-center gap-1"
              >
                <IconPlus size={18} className="text-green shrink-0" />
                <Text fz="xs" fw={600} c="gray.5">
                  {t("addOtherPartyInfo")}
                </Text>
              </UnstyledButton>
            ) : (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600} fz="sm">
                    {t("otherPartyInfo")}
                  </Text>
                  <ActionIcon
                    variant="transparent"
                    onClick={handleRemoveOtherParty}
                    c="gray.5"
                    aria-label="remove-other-party"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
                <TextInput
                  {...form.getInputProps("otherPartyName")}
                  label={t("otherPartyName")}
                  placeholder={t("otherPartyName")}
                />
                <TextInput
                  {...form.getInputProps("otherPartyPhone")}
                  label={t("otherPartyPhone")}
                  placeholder={t("otherPartyPhonePlaceholder")}
                />
                <TextInput
                  {...form.getInputProps("otherPartyEmail")}
                  label={t("otherPartyEmail")}
                  placeholder={t("otherPartyEmail")}
                />
                <Textarea
                  {...form.getInputProps("otherPartyOtherInfo")}
                  label={t("otherPartyOtherInfo")}
                  placeholder={t("otherPartyOtherInfoPlaceholder")}
                  rows={3}
                />
              </Stack>
            )}

            <div>
              <Text fw={600} fz="sm" mb={8}>
                {t("photosOfDamageYourVehicle")}
              </Text>
              <FeatureUploadImages
                documents={form.values.vehiclePhotos}
                onImageUpload={appendVehiclePhoto}
                onRemoveImage={removeVehiclePhoto}
              />
            </div>

            <div>
              <Text fw={600} fz="sm" mb={8}>
                {t("photosOfDamageOtherParty")}
              </Text>
              <FeatureUploadImages
                documents={form.values.otherPartyPhotos}
                onImageUpload={appendOtherPartyPhoto}
                onRemoveImage={removeOtherPartyPhoto}
              />
            </div>
          </Stack>
        </FormModal>
      )}
    </>
  );
};
